import React, { useEffect, useState } from 'react'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { Form, Row, Col } from 'react-bootstrap'

import SelectionField from '../common/SelectionField'
import API from '../../utils/API'
import labels from '../../constants/labels'

const DEFAULT_LIMITS_FORM = {
  beforeEvent: '0',
  afterEvent: '0',
  minimumNotice: '0',
  minimumNoticeUnit: 'hours',
  timeSlotInterval: 'default',
  maxBookingPerDay: '5',
}

const stringifyValue = (value, fallback = '0') => {
  if (value === 0) return '0'
  if (value === undefined || value === null || value === '') {
    return fallback
  }
  return String(value)
}

const formatLimitsResponse = (payload = {}) => {
  const { limits = {} } = payload || {}
  return {
    beforeEvent: stringifyValue(limits.bufferBefore),
    afterEvent: stringifyValue(limits.bufferAfter),
    minimumNotice: stringifyValue(limits.minNoticeValue),
    minimumNoticeUnit: limits.minNoticeUnit || 'hours',
    timeSlotInterval:
      limits.timeSlotInterval === undefined ||
      limits.timeSlotInterval === null ||
      limits.timeSlotInterval === ''
        ? 'default'
        : String(limits.timeSlotInterval),
    maxBookingPerDay: stringifyValue(limits.maxBookingsPerDay, '5'),
  }
}

const LimitsTab = () => {
  const [limitsForm, setLimitsForm] = useState(DEFAULT_LIMITS_FORM)
  const [initialLimits, setInitialLimits] = useState(DEFAULT_LIMITS_FORM)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const Error = useErrorService()
  const Notification = useNotificationService()

  useEffect(() => {
    const fetchLimits = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.AdminAPI.scheduling.getCoachCalendarLimits()
        const formatted = formatLimitsResponse(data)
        setLimitsForm(formatted)
        setInitialLimits(formatted)
      } catch (error) {
        Error.showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchLimits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFieldChange = (field, value) => {
    setLimitsForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  const buildPayload = () => {
    return {
      limits: {
        bufferBefore: toNumber(limitsForm.beforeEvent),
        bufferAfter: toNumber(limitsForm.afterEvent),
        minNoticeValue: toNumber(limitsForm.minimumNotice),
        minNoticeUnit: limitsForm.minimumNoticeUnit,
        timeSlotInterval:
          limitsForm.timeSlotInterval === 'default'
            ? 0
            : toNumber(limitsForm.timeSlotInterval),
        maxBookingsPerDay: toNumber(limitsForm.maxBookingPerDay),
      },
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const payload = buildPayload()
      const { data } = await API.AdminAPI.scheduling.updateCoachCalendarLimits(
        payload,
      )
      const formatted = formatLimitsResponse(data)
      setLimitsForm(formatted)
      setInitialLimits(formatted)
      Notification.showNotification(
        'Calendar limits updated successfully.',
        'success',
      )
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setLimitsForm(initialLimits)
  }

  const isFormDisabled = isLoading || isSubmitting

  return (
    <div>
      <h3 className='s3 mb-4'>Add Limits</h3>
      <>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className='mb-4'>
                <Form.Label className='input-lable'>BEFORE EVENT</Form.Label>
                <SelectionField
                  options={labels.bufferTimeOptions}
                  optionplaceholder={undefined}
                  isUseFormikSelectionField={false}
                  size='lg'
                  getValueAsOption
                  hidePlaceholder
                  value={limitsForm.beforeEvent}
                  onChange={(event) =>
                    handleFieldChange('beforeEvent', event.target.value)
                  }
                  disabled={isFormDisabled}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className='mb-4'>
                <Form.Label className='input-lable'>AFTER EVENT</Form.Label>
                <SelectionField
                  options={labels.bufferTimeOptions}
                  optionplaceholder={undefined}
                  isUseFormikSelectionField={false}
                  size='lg'
                  getValueAsOption
                  hidePlaceholder
                  value={limitsForm.afterEvent}
                  onChange={(event) =>
                    handleFieldChange('afterEvent', event.target.value)
                  }
                  disabled={isFormDisabled}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className='mb-4'>
                <Form.Label className='input-lable'>MINIMUM NOTICE</Form.Label>
                <div className='d-flex align-items-center'>
                  <Form.Control
                    type='number'
                    size='lg'
                    className='form-control mr-2 limits-form-control'
                    style={{ width: '100px' }}
                    value={limitsForm.minimumNotice}
                    onChange={(event) =>
                      handleFieldChange('minimumNotice', event.target.value)
                    }
                    min='0'
                    disabled={isFormDisabled}
                  />
                  <SelectionField
                    options={labels.timeUnitOptions}
                    optionplaceholder='Select unit'
                    isUseFormikSelectionField={false}
                    size='lg'
                    getValueAsOption
                    hidePlaceholder
                    value={limitsForm.minimumNoticeUnit}
                    onChange={(event) =>
                      handleFieldChange('minimumNoticeUnit', event.target.value)
                    }
                    style={{ flex: 1 }}
                    disabled={isFormDisabled}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className='mb-4'>
                <Form.Label className='input-lable'>
                  TIME-SLOT INTERVALS
                </Form.Label>
                <SelectionField
                  options={labels.timeSlotIntervalOptions}
                  optionplaceholder='Select interval'
                  isUseFormikSelectionField={false}
                  size='lg'
                  getValueAsOption
                  hidePlaceholder
                  value={limitsForm.timeSlotInterval}
                  onChange={(event) =>
                    handleFieldChange('timeSlotInterval', event.target.value)
                  }
                  disabled={isFormDisabled}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className='mb-4'>
                <Form.Label className='input-lable'>
                  MAXIMUM BOOKING PER DAY
                </Form.Label>
                <Form.Control
                  type='number'
                  size='lg'
                  className='form-control limits-form-control'
                  value={limitsForm.maxBookingPerDay}
                  onChange={(event) =>
                    handleFieldChange('maxBookingPerDay', event.target.value)
                  }
                  min='0'
                  placeholder='5'
                  disabled={isFormDisabled}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className='d-flex justify-content-end mt-5'>
            <Button
              variant='outline-dark'
              className='mr-3 text-uppercase s8'
              onClick={handleCancel}
              disabled={isFormDisabled}>
              Cancel
            </Button>
            <Button
              variant='saffron'
              className='save-btn-fixed-width'
              onClick={handleSubmit}
              isLoading={isSubmitting || isLoading}
              disabled={isFormDisabled}>
              Save
            </Button>
          </div>
        </Form>
      </>
    </div>
  )
}

export default LimitsTab
